import { lazy, Suspense, useEffect, useState } from 'react';
import { STRINGS } from '../../Constants/ConstantStrings';
import ConfirmationPopupModal from '../../SharedComponets/ConfirmationPopupModal';
import NoDataFound from '../../SharedComponets/NoDataFound';
import {
  deleteQuestion,
  requestQuestionsclosureData
} from '../../Store/reducers/ClosureQuestions';
import { useReduxDispatch, useReduxSelector } from '../../Store/reduxHooks';
import { NoQuestionsIcon } from '../../Universal/Assets';
import { colorState } from '../../Universal/Foundation';
import { Button, Icon } from '../../Universal/NovusDSImports';
import {
  btnStyles,
  confirmPopupModalStyles
} from '../../Universal/NovusDSImports/variants';
import { RootState } from '../../store';
import PALoader from '../../SharedComponets/PALoader';
import { getPermission } from '../../CommonUtilities/CommonUtilities';
import { TooltipComponent } from '../../SharedComponets/Tooltip';

const GenericTableRT7 = lazy(
  () => import('../GenericTableComponent/GenericTable')
);

export const EventQuestionTable = (props: any) => {
  const { addQuestion, updatedTableData, setUpdatedTableData } = props;
  const dispatch = useReduxDispatch(),
    [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false),
    [deleteData, setDeleteData] = useState<any>(),
    selectedClosure = useReduxSelector(
      (state: RootState) => state.Common.selectedClosure
    ),
    questionsclosuresData = useReduxSelector(
      (state: RootState) => state.Questions.closuresQuestionsData
    ),
    currentUser = useReduxSelector(
      (state: RootState) => state.Common.currentUserDetails
    ),
    isFetchingQuestionsclosuresData = useReduxSelector(
      (state: RootState) => state.Questions.isFetchingQuestionsLibraryData
    );
  const isAdmin = useReduxSelector(
    (state: RootState) =>
      state?.Common?.currentUserDetails?.user_permission_set?.role_names
  )?.includes('Admin');

  const deleteLibraryQuestion = (data: any) => {
    setIsDeleteModalOpen(true);
    setDeleteData(data);
  };

  const columns = [
    {
      Header: 'Questions',
      accessor: 'display_name',
      Cell: ({ row }) => (
        <div key={row} className="event-questions-wrapper">
          {row.original.display_name}
          {row.original.default && ' *'}
          {row.original.multiple_choices && (
            <ul>
              {row.original.multiple_choices.map((item: any, index: number) => {
                return <li key={index}>{item}</li>;
              })}
            </ul>
          )}
        </div>
      ),
      label: ({ row }) => `
            
          Question : ${row.original.display_name}

          ${row.original.default ? ' Default Question.' : ''}

          ${
            row.original.multiple_choices?.length
              ? ' Choices are ' +
                row.original.multiple_choices
                  .map((item: any) => item)
                  .join(', ')
              : ''
          }
          
          `
    },
    {
      Header: STRINGS.TYPE,
      Cell: ({ row }) => (
        <div className="text-capitalize">
          {row.original.field_type === 'numeric'
            ? STRINGS.NUMERIC
            : STRINGS.CHOICE}
        </div>
      ),
      label: ({ row }) =>
        STRINGS.TYPE +
        ' : ' +
        (row.original.field_type === 'numeric'
          ? STRINGS.NUMERIC
          : STRINGS.CHOICE)
    },
    {
      Header: STRINGS.ABBREVIATED,
      accessor: 'abbr_name',
      label: ({ row }) => STRINGS.ABBREVIATED + ' : ' + row.original.abbr_name
    },

    {
      Header: STRINGS.ACTIONS,
      Cell: ({ row }) => {
        return (
          <div
            className="view-details-section gap-3"
            aria-label="action-buttons"
          >
            <span className="visually-hidden">{STRINGS.ACTIONS}</span>
            {getPermission(currentUser, 'change_closureticket') && (
              <div
                aria-label={`delete question ${row.original.display_name}`}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    if (isAdmin || !row.original.default) {
                      deleteLibraryQuestion(row.original);
                    }
                  }
                }}
                onClick={() => {
                  if (isAdmin || !row.original.default) {
                    deleteLibraryQuestion(row.original);
                  }
                }}
                className={` ${
                  isAdmin
                    ? 'me-2 me-md-5 action-icons'
                    : row.original.default
                    ? 'disabled me-2 me-md-5'
                    : 'me-2 me-md-5 action-icons'
                }`}
                data-tooltip-id={`delete-${row.original.id}`}
              >
                <Icon
                  icon={'trash_01'}
                  className={`${
                    isAdmin ? '' : row.original.default && 'disabled'
                  } trash`}
                  height={20}
                  width={20}
                  stroke={
                    isAdmin
                      ? colorState.icon.default.primary
                      : row.original.default && colorState.icon.default.disabled
                  }
                >
                  {STRINGS.DELETE}
                </Icon>
                {!row.original.default && (
                  <TooltipComponent id={`delete-${row.original.id}`}>
                    {STRINGS.DELETE}
                  </TooltipComponent>
                )}
              </div>
            )}
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    if (
      selectedClosure &&
      getPermission(currentUser, 'view_closurequestions')
    ) {
      dispatch(
        requestQuestionsclosureData({
          closureId: selectedClosure?.id,
          limit: 'all'
        })
      );
    }
  }, [dispatch, selectedClosure, currentUser]);

  const customConfirmationStyles = {
    content: {
      minWidth: '20%',
      minHeight: '10vh'
    },
    overlay: {
      backgroundColor: 'rgba(114, 114, 114, 0.294)'
    }
  };

  const postModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const submit = () => {
    const params = {
      closureId: selectedClosure?.id,
      questionId: deleteData.id
    };
    const activeTab = 'event';
    dispatch(deleteQuestion({ params, activeTab }));
    setIsDeleteModalOpen(false);
  };

  const renderConfirmationFooter = () => {
    return (
      <div className="gap-3 d-flex">
        <Button
          onClick={() => setIsDeleteModalOpen(false)}
          {...btnStyles.default}
        >
          {STRINGS.CANCEL}
        </Button>
        <Button onClick={submit} {...btnStyles.danger}>
          {STRINGS.YES}
        </Button>
      </div>
    );
  };

  const NoQuestionsData = () => {
    return (
      <NoDataFound
        src={NoQuestionsIcon}
        isAbsolute
        description={STRINGS.YOUR_LIST_OF_QUESTIONS_ARE_EMPTY}
        alt={'no-questions-img'}
        subMsgDescription={
          STRINGS.YOUR_LIST_OF_QUESTIONS_ARE_EMPTY_BECAUSE_YOU_HAVENT_ADDED_ANY_QUESTIONS_YET
        }
        btn={
          <Button {...btnStyles.primary} onClick={() => addQuestion(false)}>
            <Icon
              icon={'add'}
              stroke={colorState.icon.default['primary-inverse']}
            />
            {STRINGS.ADD_NEW_QUESTION}
          </Button>
        }
      />
    );
  };

  return (
    <>
      <div className="w-100 h-100 d-flex overflow-auto flex-grow-1 flex-column">
        {!questionsclosuresData ? (
          <PALoader />
        ) : (
          <Suspense fallback={<PALoader />}>
            <GenericTableRT7
              columns={columns}
              tableData={questionsclosuresData}
              shouldFocusColumnOfRow
              showCheckBoxColumn={false}
              noDataFoundScreen={
                !isFetchingQuestionsclosuresData ? NoQuestionsData() : <></>
              }
              isDraggable={isAdmin}
              updatedTableData={updatedTableData}
              setUpdatedTableData={setUpdatedTableData}
            />
          </Suspense>
        )}
      </div>
      <ConfirmationPopupModal
        isOpen={isDeleteModalOpen}
        showHeader={true}
        title={STRINGS.DELETE}
        description={STRINGS.ARE_YOU_SURE_WANT_TO_DELETE_QUESTION}
        showFooter={renderConfirmationFooter()}
        style={customConfirmationStyles}
        closeModal={postModalClose}
        showBorderTop={false}
        isBodyPaddingNone={true}
        customFooter
        {...confirmPopupModalStyles}
      />
    </>
  );
};
